<template>
  <div>
      <panel-item>
        <template v-if="occurs_at_interval && custom_interval">
          <panel-title>
            Repeat Every
          </panel-title>
          <multi-input full compress>
            <column>
              <div class="sr-only">
                <label for='interval_amount' class="a-select-label">Interval Amount</label>
              </div>
              <number-input
                v-model="interval.amount"
                :min="1"
                id="interval_amount"
              ></number-input>
            </column>
            <column>
              <div class="sr-only">
                <label for="interval_unit" class="a-select-label">Interval Unit</label>
              </div>
              <a-select
                v-model="interval.unit"
                :options="unitOptions"
                compress
                id="interval_unit"
              ></a-select>
            </column>
          </multi-input>
          <label>after first trigger</label>
        </template>
        <template v-if="interval.unit === DurationUnit.year ">
          <panel-title>On what day?</panel-title>
          <double-date-select :document="interval"
                              :day="dayOptions"
                              compress
                              :month="monthOptions"
                              :key="`window-close-${interval.day}-${interval.month}`"
          ></double-date-select>
        </template>
        <template v-if="interval.unit === DurationUnit.month">
          <label for="day">Monthly on the 1st, Monthly on the 2nd, etc</label>
            <number-input :min="1" :max="31"
            v-model="interval.day"
            id="day"
            ></number-input>
        </template>
        <template v-if="interval.unit === DurationUnit.week">
          <panel-title>On what day?</panel-title>
          <div class="day-btn-group">
            <button v-for="weekday in weekDays"
                    v-tooltip="weekdayName[weekday]"
                    class="btn-icon square md"
                    :class="{blue: interval.weekdayIsSelected(weekday)}"
                    @click="handleWeekdayClick(weekday)">
                    <span class="icon-text">{{weekdayName[weekday].charAt(0)}}</span>
            </button>
          </div>
        </template>
        <template>
          <label>At what time?</label>
          <double-select
            :document="interval"
            compress
            :selectors="timeSelectors"
            :key="`close-time-selectors-${interval.time_of_day}-${interval.time_am_or_pm}`">
          </double-select>
        </template>

      </panel-item>
      <panel-item>
        <label :for="interval.idForProp('end_type')">
          When does the event end?
          <help-tooltip>Please read the documentation on how this works.</help-tooltip>
        </label>
        <a-select
          v-model="interval.end_type"
          :options="endTypeOptions"
          compress
          :id="interval.idForProp('end_type')"
        ></a-select>
      </panel-item>
      <panel-item v-if="interval.end_type === IntervalEndType.SpecificDate">
        <div class="sr-only">
          <label for='end_date' class="a-select-label">End Date</label>
        </div>
        <date-picker
          v-model="interval.end_date"
          :mode="DatetimeMode.Date"
          id="end_date"
        ></date-picker>
      </panel-item>
      <panel-item v-if="interval.end_type === IntervalEndType.NumberOfOccurrences">
        <div class="sr-only">
          <label for='number_of_occurrences' class="a-select-label">Number of Occurrences</label>
        </div>
        <number-input id="number_of_occurrences"
        v-model="interval.number_of_occurrences"
        ></number-input>
      </panel-item>
      <panel-item v-if="interval.end_type === IntervalEndType.OnCondition">
        <conditional-wrapper
          :parent="interval"
          :field-definition="ObjectModelConditionalFields.INTERVAL_END_CONDITION"
          :has-otherwise-value="false"
          :can-add-rule="Object.keys(interval.end_condition_conditional_rule_sets).length === 0">
          <template #header>
            On Condition
          </template>
          <template #value="slotProps">
            <input type="hidden"
                   v-model="slotProps.model[slotProps.fieldName]"
                   value="true">
          </template>
        </conditional-wrapper>
      </panel-item>
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import {DurationType} from "../../../common/enums/durationType";
import {statusActivationDateComparisonVOptions, statusActivationEventVOptions} from "~/helpers/statusActivationHelper";
import DurationAndTimeHelper from "~/helpers/durationAndTimeHelper";
import {DurationUnit} from "~/enums/durationUnit";
import DoubleSelect from "~/components/doubleSelect.vue"
import Interval from "~/models/interval";
import {WeekDay, weekdayName, weekDays} from "~/enums/weekday"
import DoubleDateSelect from "~/components/doubleDateSelect.vue"
import {IntervalEndType} from "~/enums/intervalEndType"
import {DatetimeMode} from "~/models/datetimeMode";
import ObjectModelConditionalFields from "~/models/objectModels/objectModelConditionalFields";
import ConditionalWrapper from "~/components/conditionalRuleBuilder/objectModelPropertyConditionalWrapper.vue"
import organizationMixin from "~/mixins/organizationMixin";
import {ObjectModelTriggerRunsForType} from "~/enums/objectModelTriggerRunsForType";
import selectedMixin from "../../../formability/object_model_builder/mixins/selectedMixin";

export default Vue.extend({
    props: {
      interval: {
        required: true,
        type: Interval
      },
      occurs_at_interval: {
        required: true
      },
      custom_interval: {
        required: true
      },
    },
    mixins: [organizationMixin, selectedMixin],
    components: {
      DoubleSelect,
      DoubleDateSelect,
      ConditionalWrapper
    },
    data: function () {
      return {
        editedConfirmation: null,
        DurationType: DurationType,
        statusActivationDateComparisonVOptions,
        statusActivationEventVOptions,
        DurationUnit,
        weekDays,
        weekdayName,
        IntervalEndType,
        DatetimeMode,
        ObjectModelConditionalFields: ObjectModelConditionalFields

      };
    },
    computed: {
      unitOptions() {
        return DurationAndTimeHelper.durationUnitLongOptions
      },
      monthOptions() {
        return {
          label: "Month",
          modelField: "month",
        }
      },
      dayOptions() {
        return {
          label: "Day",
          modelField: "day"
        }
      },
      timeSelectors() {
        return [
          {options: DurationAndTimeHelper.hoursOfDayOptionsTwelveHour, label: "Time of day", modelField: "time_of_day"},
          {options: DurationAndTimeHelper.minuteOptions, label: "Minute", modelField: "time_minute"},
          {options: DurationAndTimeHelper.timeAmOrPm, label: "Am or Pm", modelField: "time_am_or_pm"}
        ]
      },
      endTypeOptions(){
        const options = [
          {label: "Never" , value: IntervalEndType.Never},
          {label: "On specific date" , value: IntervalEndType.SpecificDate},
          {label: "Number of Occurrences" , value: IntervalEndType.NumberOfOccurrences},
        ]

        if (this.selectedObjectModelTrigger.trigger_runs_for_type !== ObjectModelTriggerRunsForType.Collection) {
          options.push({label: "On Condition", value: IntervalEndType.OnCondition})
        }

        return options
      }
    },
    methods: {
      handleWeekdayClick(weekday: WeekDay) {
        let newWeekdays = [];
        if (this.interval.weekdayIsSelected(weekday)) {
          newWeekdays = this.interval.weekdays.filter(x => x !== weekday);
        } else {
          newWeekdays = [...this.interval.weekdays, weekday];
        }
        this.interval.weekdays = newWeekdays;
        Vue.set(this.interval, "weekdays", newWeekdays);
      }
    }

  });
</script>